import React, { useEffect, useState } from "react"
import { apiGet, apiPost } from "../api"
import serverName from "../serverName";
import {applyPlaceholders} from "./Rules";

export default function EditRules() {
    const [data, setData] = useState(null)
    const [sel, setSel] = useState(0)
    const [dirty, setDirty] = useState(false)
    const [saving, setSaving] = useState(false)
    const [loading, setLoading] = useState(true)
    const [toast, setToast] = useState(null)

    const showToast = m => { setToast(m); setTimeout(() => setToast(null), 1500) }

    const normalize = d => {
        if (!d?.sections) return d
        const sections = d.sections.map(s => {
            const items = (s.items || []).map(it => {
                if (typeof it === "string") return { rule_name: "", text: it }
                const rn = typeof it?.rule_name === "string" ? it.rule_name : ""
                const tx = typeof it?.text === "string" ? it.text : ""
                return { rule_name: rn, text: tx }
            })
            return { name: s.name, items }
        })
        return { ...d, sections }
    }

    const load = async () => {
        setLoading(true)
        try {
            const res = await apiGet(`/rules_get.php?ts=${Date.now()}`)
            if (!res?.ok || !res?.rules?.sections) throw new Error()
            const norm = normalize(res.rules)
            setData(norm)
            setSel(0)
            setDirty(false)
        } catch {
            showToast("Načtení selhalo")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { load() }, [])

    const setD = fn => {
        setData(prev => {
            const next = typeof fn === "function" ? fn(prev) : fn
            setDirty(true)
            return next
        })
    }

    const save = async () => {
        if (!data) return
        setSaving(true)
        const res = await apiPost("/rules_save.php", data)
        setSaving(false)
        if (res?.ok) { setDirty(false); showToast("Uloženo") }
        else showToast(res?.error || "Chyba při ukládání")
    }

    if (loading) return <div className="px-6 py-20 text-center text-white/70">Načítám…</div>
    if (!data)   return <div className="px-6 py-20 text-center text-red-300">Nelze načíst pravidla</div>

    const addSection = () => {
        setD(d => ({ ...d, sections: [...d.sections, { name: "Nová sekce", items: [{ rule_name: "Název bodu", text: "Nový bod" }] }] }))
        setSel(data.sections.length)
    }
    const rmSection = i => {
        const arr = data.sections.filter((_, x) => x !== i)
        setD({ ...data, sections: arr })
        setSel(Math.max(0, i - 1))
    }
    const mvSection = (i, dir) => {
        const t = i + dir
        if (t < 0 || t >= data.sections.length) return
        const arr = [...data.sections]
        ;[arr[i], arr[t]] = [arr[t], arr[i]]
        setD({ ...data, sections: arr })
        setSel(t)
    }
    const setSectionName = (i, v) => {
        const arr = [...data.sections]
        arr[i] = { ...arr[i], name: v }
        setD({ ...data, sections: arr })
    }
    const addItem = i => {
        const arr = [...data.sections]
        arr[i] = { ...arr[i], items: [...arr[i].items, { rule_name: "Název bodu", text: "Nový bod" }] }
        setD({ ...data, sections: arr })
    }
    const setItemName = (i, j, v) => {
        const arr = [...data.sections]
        const items = [...arr[i].items]
        items[j] = { ...items[j], rule_name: v }
        arr[i] = { ...arr[i], items }
        setD({ ...data, sections: arr })
    }
    const setItemText = (i, j, v) => {
        const arr = [...data.sections]
        const items = [...arr[i].items]
        items[j] = { ...items[j], text: v }
        arr[i] = { ...arr[i], items }
        setD({ ...data, sections: arr })
    }
    const rmItem = (i, j) => {
        const arr = [...data.sections]
        arr[i] = { ...arr[i], items: arr[i].items.filter((_, x) => x !== j) }
        setD({ ...data, sections: arr })
    }
    const mvItem = (i, j, dir) => {
        const t = j + dir
        const items = [...data.sections[i].items]
        if (t < 0 || t >= items.length) return
            ;[items[j], items[t]] = [items[t], items[j]]
        const arr = [...data.sections]
        arr[i] = { ...arr[i], items }
        setD({ ...data, sections: arr })
    }

    return (
        <div className="min-h-screen bg-neutral-950 text-white">
            <div className="sticky top-0 z-20 bg-neutral-950/90 backdrop-blur border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
                    <input
                        value={data.title}
                        onChange={e => setD({ ...data, title: e.target.value })}
                        className="text-2xl sm:text-3xl font-bold bg-transparent outline-none text-white tracking-wide"
                    />
                    <span className={`px-2 py-1 text-xs rounded-full ${dirty ? "bg-yellow-400/10 text-yellow-300 border border-yellow-300/20" : "bg-emerald-400/10 text-emerald-300 border border-emerald-300/20"}`}>
                        {dirty ? "Neuloženo" : "Uloženo"}
                    </span>
                    <div className="ml-auto flex items-center gap-2">
                        <button onClick={load} className="px-3 py-2 rounded-xl border border-white/10 bg-white/5 text-white text-sm">Načíst ze serveru</button>
                        <button onClick={save} disabled={saving} className="px-4 py-2 rounded-xl bg-fuchsia-600 hover:bg-fuchsia-500 disabled:opacity-60 text-white text-sm">{saving ? "Ukládám…" : "Uložit"}</button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-10 space-y-10">
                <div className="rounded-2xl border border-white/10 bg-neutral-900/40 p-6">
                    <h2 className="text-lg font-semibold mb-4">Editor</h2>
                    <div className="margin-bottom-5 text-gray-500">Pro vypsání názvu serveru ({serverName}) využij <b>%serverName%</b></div>
                    <div className="grid grid-cols-1 md:grid-cols-[260px,1fr] gap-6">
                        <div className="space-y-2">
                            {data.sections.map((s, i) => (
                                <button
                                    key={i}
                                    onClick={() => setSel(i)}
                                    className={`w-full text-left px-4 py-3 rounded-xl border ${sel === i ? "border-fuchsia-400 bg-fuchsia-900/20" : "border-white/10 bg-white/5 hover:bg-white/10"}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="truncate">{s.name}</span>
                                        <span className="text-xs text-white/60">{s.items.length}</span>
                                    </div>
                                </button>
                            ))}
                            <button onClick={addSection} className="mt-2 w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white text-sm">Přidat sekci</button>
                        </div>

                        <div className="rounded-xl border border-white/10 bg-neutral-950 p-4">
                            <div className="flex items-center gap-2 mb-4">
                                <input
                                    value={data.sections[sel].name}
                                    onChange={e => setSectionName(sel, e.target.value)}
                                    className="flex-1 bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white"
                                />
                                <button onClick={() => mvSection(sel, -1)} className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm">↑</button>
                                <button onClick={() => mvSection(sel, 1)} className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm">↓</button>
                                <button onClick={() => rmSection(sel)} className="px-3 py-2 rounded-lg bg-red-500/10 text-red-300 text-sm">Smazat</button>
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-xs uppercase tracking-widest text-white/60">Body</h3>
                                    <button onClick={() => addItem(sel)} className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm">Přidat bod</button>
                                </div>

                                {data.sections[sel].items.map((it, j) => (
                                    <div key={j} className="flex items-start gap-3">
                                        <div className="flex-1 space-y-2">
                                            <input
                                                value={it.rule_name}
                                                onChange={e => setItemName(sel, j, e.target.value)}
                                                placeholder="Název bodu"
                                                className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white"
                                            />
                                            <textarea
                                                value={it.text}
                                                onChange={e => setItemText(sel, j, e.target.value)}
                                                className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white min-h-[64px]"
                                            />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <button onClick={() => mvItem(sel, j, -1)} className="px-3 py-2 rounded-lg bg-white/10 text-white text-xs">↑</button>
                                            <button onClick={() => mvItem(sel, j, 1)} className="px-3 py-2 rounded-lg bg-white/10 text-white text-xs">↓</button>
                                            <button onClick={() => rmItem(sel, j)} className="px-3 py-2 rounded-lg bg-red-500/10 text-red-300 text-xs">Smazat</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-neutral-900/40 p-6">
                    <h2 className="text-lg font-semibold mb-6">Náhled</h2>
                    <div className="space-y-6">
                        {data.sections.map((sec, i) => (
                            <div key={i} className="rounded-xl p-5 bg-white/5 border border-white/10">
                                <h3 className="text-2xl mb-3 font-semibold">{sec.name}</h3>
                                <ul className="space-y-2 text-white/80">
                                    {sec.items.map((x, j) => (
                                        <li key={j} className="flex items-start gap-3 pb-5">
                                            <span className="mt-1 h-2 w-2 rounded-full bg-fuchsia-400" />
                                            <span>
                                                <div className="text-xl"><b dangerouslySetInnerHTML={{ __html: applyPlaceholders(x.rule_name) }}></b></div>
                                                <div className="text-base" dangerouslySetInnerHTML={{ __html: applyPlaceholders(x.text) }}></div>
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {toast && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-3 rounded-xl border border-white/10 bg-neutral-900/90 text-white">
                    {toast}
                </div>
            )}
        </div>
    )
}
